import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle, User } from 'lucide-react';
import { clientsApi } from '../api/organizations';

export default function ActivatePage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const nav = useNavigate();
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [pdConsent, setPdConsent] = useState(false);
  const [feedbackConsent, setFeedbackConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [info, setInfo] = useState<{ email: string; full_name: string } | null>(null);
  const [infoErr, setInfoErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    clientsApi.activateInfo(token).then(setInfo).catch((e) => {
      setInfoErr(e?.response?.data?.detail || 'Ссылка недействительна или истекла');
    });
  }, [token]);

  if (!token) return <Navigate to="/login" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwd !== pwd2) { setErr('Пароли не совпадают'); return; }
    if (pwd.length < 6) { setErr('Минимум 6 символов'); return; }
    if (!pdConsent) { setErr('Требуется согласие на обработку персональных данных'); return; }
    setLoading(true); setErr(null);
    try {
      const r = await clientsApi.activate(token, pwd, pdConsent, feedbackConsent);
      setDone(r.email);
      setTimeout(() => nav('/login'), 2000);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-white mb-1">Активация аккаунта</h1>
        <p className="text-sm text-slate-400 mb-4">Установите пароль для входа в Service Desk.</p>

        {infoErr && (
          <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm mb-4 flex items-center gap-2">
            <AlertCircle size={14} /> {infoErr}
          </div>
        )}

        {info && (
          <div className="bg-slate-950 border border-slate-700 rounded px-3 py-2 mb-4 flex items-center gap-2 text-sm">
            <User size={14} className="text-blue-400" />
            <div>
              <div className="text-white">{info.full_name || '(имя не указано)'}</div>
              <div className="text-xs text-slate-400 font-mono">{info.email}</div>
            </div>
          </div>
        )}

        {done ? (
          <div className="bg-emerald-900/20 border border-emerald-800 text-emerald-300 px-4 py-3 rounded flex items-center gap-2">
            <CheckCircle size={16} />
            Готово, <span className="font-mono">{done}</span>. Перенаправляю на вход…
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Пароль</span>
              <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} minLength={6}
                     className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Повторите</span>
              <input type="password" value={pwd2} onChange={e => setPwd2(e.target.value)}
                     className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none" />
            </label>

            {/* Согласия — обязательное (ПД) и опциональное (обратная связь). */}
            <div className="space-y-2 pt-1">
              <label className="flex gap-2 cursor-pointer items-start">
                <input type="checkbox" required checked={pdConsent}
                       onChange={e => setPdConsent(e.target.checked)}
                       className="mt-0.5 accent-blue-600" />
                <span className="text-xs text-slate-300 leading-relaxed">
                  Я даю согласие на обработку моих персональных данных
                  в соответствии с{' '}
                  <a href="https://valocloud.ru/documents/consent_pd.docx"
                     target="_blank" rel="noreferrer"
                     className="text-blue-400 hover:text-blue-300 underline">Согласием</a>
                  {' '}и{' '}
                  <a href="https://valocloud.ru/documents/policy_iipdn_24.11.2025.docx"
                     target="_blank" rel="noreferrer"
                     className="text-blue-400 hover:text-blue-300 underline">Политикой обработки ПД</a>.
                  <span className="text-red-400 ml-1">*</span>
                </span>
              </label>
              <label className="flex gap-2 cursor-pointer items-start">
                <input type="checkbox" checked={feedbackConsent}
                       onChange={e => setFeedbackConsent(e.target.checked)}
                       className="mt-0.5 accent-blue-600" />
                <span className="text-xs text-slate-400 leading-relaxed">
                  Согласен(на) получать от ValoCloud сообщения по
                  обращениям в поддержку и информацию о продукте
                  по email, в Telegram и MAX. Можно отозвать в любой момент.
                </span>
              </label>
            </div>

            {err && (
              <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm flex items-center gap-2">
                <AlertCircle size={14} /> {err}
              </div>
            )}
            <button type="submit" disabled={loading || !pwd || !pdConsent}
                    className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white font-bold py-2 rounded inline-flex items-center justify-center gap-2">
              {loading && <Loader2 size={14} className="animate-spin" />}
              Установить пароль
            </button>
            <Link to="/login" className="block text-center text-xs text-slate-500 hover:text-slate-300">
              ← К входу
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
